import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  FiSearch,
  FiPhone,
  FiUser,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

const BASE_CONTACTS_URL =
  "https://varahibackend.varahiselfdrivecars.com/api/users";

const getContactsUrl = (userId) => {
  const selectedUserId = userId || "";
  return `${BASE_CONTACTS_URL}/${encodeURIComponent(selectedUserId)}/contacts`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
};

const ContactsRetrieved = () => {
  const { userId } = useParams();
  const activeUserId = userId || "";

  console.log("Route params:", useParams());
  console.log("User ID:", userId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    isRegistered: "",
    phoneNumber: "",
    name: "",
  });

  const getContacts = useCallback(
    async (requestFilters = {}) => {
      try {
        setLoading(true);
        setError("");
        const params = new URLSearchParams();
        if (requestFilters.isRegistered)
          params.set("isRegistered", requestFilters.isRegistered);
        if (requestFilters.phoneNumber)
          params.set("phoneNumber", requestFilters.phoneNumber);
        if (requestFilters.name) params.set("name", requestFilters.name);
        const url = `${getContactsUrl(activeUserId)}?${params.toString()}`;
        const response = await axios.get(url);
        if (!response.data?.success)
          throw new Error(
            response.data?.message || "Unable to retrieve contacts.",
          );
        setData(response.data.data);
      } catch (requestError) {
        setError(
          requestError.response?.data?.message ||
          requestError.message ||
          "Unable to retrieve contacts.",
        );
      } finally {
        setLoading(false);
      }
    },
    [activeUserId],
  );

  // Initial load
  useEffect(() => {
    getContacts({ isRegistered: "", phoneNumber: "", name: "" });
  }, [getContacts]);

  // Auto-search on filter change (with debounce for better UX)
  useEffect(() => {
    const timer = setTimeout(() => {
      getContacts(filters);
    }, 400);

    return () => clearTimeout(timer);
  }, [filters, getContacts]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    getContacts(filters);
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ background: "#f8fafc" }}
      >
        <Spinner
          animation="border"
          variant="primary"
          style={{ width: "3rem", height: "3rem" }}
        />
      </div>
    );

  if (!data)
    return (
      <Container
        fluid
        className="py-5"
        style={{ background: "#f8fafc", minHeight: "100vh" }}
      >
        {error && (
          <Alert variant="danger" className="shadow-sm">
            {error}
          </Alert>
        )}
        <Card
          className="border-0 shadow"
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            borderRadius: "16px",
          }}
        >
          <Card.Header
            className="bg-white border-0 py-3"
            style={{ borderBottom: "1px solid #eef2f6" }}
          >
            <h4 className="mb-0 fw-bold" style={{ color: "#0f172a" }}>
              Contacts Retrieved
            </h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row className="g-3 align-items-end">
                <Col md={8}>
                  <Form.Label
                    className="fw-semibold text-secondary"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Name
                  </Form.Label>
                  <Form.Control
                    name="name"
                    value={filters.name}
                    onChange={handleChange}
                    placeholder="Search by name"
                    className="border-0 bg-light"
                    style={{
                      borderRadius: "10px",
                      padding: "0.6rem 1rem",
                    }}
                  />
                </Col>
                <Col md={4}>
                  <button
                    type="submit"
                    className="btn w-100 fw-semibold"
                    style={{
                      background: "#0f172a",
                      color: "white",
                      borderRadius: "10px",
                      padding: "0.6rem",
                    }}
                  >
                    <FiRefreshCw className="me-2" /> Retry
                  </button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    );

  const { user, contacts = [], summary = {}, pagination = {} } = data;
  const currentUserName = user?.name || "User";
  const currentUserMobile = user?.mobile || user?.phone || "N/A";

  return (
    <Container
      fluid
      className="py-4"
      style={{ background: "#f1f5f9", minHeight: "100vh" }}
    >
      <Card
        className="border-0 shadow-sm"
        style={{ borderRadius: "20px", overflow: "hidden" }}
      >
        {/* Header */}
        <Card.Header
          className="bg-white border-0 py-4 px-4"
          style={{ borderBottom: "1px solid #eef2f6" }}
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center">
              <div
                style={{
                  background: "#eef2ff",
                  padding: "10px",
                  borderRadius: "14px",
                  marginRight: "16px",
                }}
              >
                <FiUser size={24} color="#4f46e5" />
              </div>
              <div>
                <h4
                  className="mb-0 fw-bold"
                  style={{ color: "#0f172a", letterSpacing: "-0.3px" }}
                >
                  Contacts Retrieved
                </h4>
                <small className="text-muted" style={{ fontSize: "0.8rem" }}>
                  {currentUserName} • {currentUserMobile}
                </small>
              </div>
            </div>

            {/* <div className="text-end" style={{ minWidth: "180px" }}>
              <div
                className="fw-semibold text-secondary"
                style={{ fontSize: "0.72rem" }}
              >
                User Details
              </div>
              <div
                className="fw-bold"
                style={{ color: "#0f172a", fontSize: "0.95rem" }}
              >
                {currentUserName}
              </div>
              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                {currentUserMobile}
              </div>
            </div> */}
          </div>
        </Card.Header>

        {/* Filters */}
        <Card.Body
          className="bg-white border-0 px-4 py-3"
          style={{ borderBottom: "1px solid #f1f5f9" }}
        >
          <Form onSubmit={handleSearch}>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label
                  className="fw-semibold text-secondary"
                  style={{ fontSize: "0.8rem", letterSpacing: "0.3px" }}
                >
                  <FiUser className="me-1" size={14} /> Name
                </Form.Label>
                <Form.Control
                  name="name"
                  value={filters.name}
                  onChange={handleChange}
                  placeholder="Search by name"
                  className="border-0 bg-light"
                  style={{
                    borderRadius: "10px",
                    padding: "0.6rem 1rem",
                    fontSize: "0.9rem",
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary"
                  style={{ fontSize: "0.8rem", letterSpacing: "0.3px" }}
                >
                  <FiPhone className="me-1" size={14} /> Phone
                </Form.Label>
                <Form.Control
                  name="phoneNumber"
                  value={filters.phoneNumber}
                  onChange={handleChange}
                  placeholder="Phone number"
                  className="border-0 bg-light"
                  style={{
                    borderRadius: "10px",
                    padding: "0.6rem 1rem",
                    fontSize: "0.9rem",
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary"
                  style={{ fontSize: "0.8rem", letterSpacing: "0.3px" }}
                >
                  Registration
                </Form.Label>
                <Form.Select
                  name="isRegistered"
                  value={filters.isRegistered}
                  onChange={handleChange}
                  className="border-0 bg-light"
                  style={{
                    borderRadius: "10px",
                    padding: "0.6rem 1rem",
                    fontSize: "0.9rem",
                  }}
                >
                  <option value="">All</option>
                  <option value="true">Registered</option>
                  <option value="false">Not registered</option>
                </Form.Select>
              </Col>
              {/* <Col md={2}>
                                <button
                                    type="submit"
                                    className="btn w-100 fw-semibold"
                                    style={{
                                        background: '#4f46e5',
                                        color: 'white',
                                        borderRadius: '10px',
                                        padding: '0.6rem',
                                        fontSize: '0.9rem',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#4338ca'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#4f46e5'}
                                >
                                    <FiSearch className="me-2" size={16} /> Search
                                </button>
                            </Col> */}
            </Row>
          </Form>
        </Card.Body>

        {/* Summary Cards */}
        {user && (
          <Card.Body
            className="bg-white px-4 py-4"
            style={{ borderBottom: "1px solid #f1f5f9" }}
          >
            <Row className="g-3">
              <Col>
                <div
                  className="p-3 rounded-3 text-center"
                  style={{ background: "#f8fafc" }}
                >
                  <div
                    className="text-muted fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Total in DB
                  </div>
                  <strong className="fs-4" style={{ color: "#0f172a" }}>
                    {summary.contactsCountInDB}
                  </strong>
                </div>
              </Col>
              <Col>
                <div
                  className="p-3 rounded-3 text-center"
                  style={{ background: "#f8fafc" }}
                >
                  <div
                    className="text-muted fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Filtered
                  </div>
                  <strong className="fs-4" style={{ color: "#0f172a" }}>
                    {summary.totalContacts}
                  </strong>
                </div>
              </Col>
              <Col>
                <div
                  className="p-3 rounded-3 text-center"
                  style={{ background: "#ecfdf5" }}
                >
                  <div
                    className="text-muted fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Registered
                  </div>
                  <strong className="fs-4" style={{ color: "#059669" }}>
                    {summary.registeredContacts}
                  </strong>
                </div>
              </Col>
              <Col>
                <div
                  className="p-3 rounded-3 text-center"
                  style={{ background: "#fef2f2" }}
                >
                  <div
                    className="text-muted fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Not registered
                  </div>
                  <strong className="fs-4" style={{ color: "#dc2626" }}>
                    {summary.nonRegisteredContacts}
                  </strong>
                </div>
              </Col>
              <Col>
                <div
                  className="p-3 rounded-3 text-center"
                  style={{ background: "#f8fafc" }}
                >
                  <div
                    className="text-muted fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Allowed
                  </div>
                  <Badge
                    bg={summary.contactsAllowed ? "success" : "secondary"}
                    style={{
                      fontSize: "1rem",
                      padding: "0.3rem 1.2rem",
                      borderRadius: "30px",
                      fontWeight: "500",
                    }}
                  >
                    {summary.contactsAllowed ? (
                      <FiCheckCircle className="me-1" />
                    ) : (
                      <FiXCircle className="me-1" />
                    )}
                    {summary.contactsAllowed ? "Yes" : "No"}
                  </Badge>
                </div>
              </Col>
            </Row>
          </Card.Body>
        )}

        {/* Table */}
        <div style={{ background: "#ffffff", overflowX: "auto" }}>
          <Table
            hover
            striped
            className="mb-0 align-middle"
            style={{ fontSize: "0.9rem" }}
          >
            <thead
              style={{
                background: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <tr>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  #
                </th>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  Contact name
                </th>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  Phone
                </th>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  Email
                </th>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  Status
                </th>
                <th
                  className="fw-semibold text-secondary"
                  style={{
                    padding: "0.9rem 0.8rem",
                    fontSize: "0.75rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  Added
                </th>
              </tr>
            </thead>
            <tbody>
              {contacts.length ? (
                contacts.map((contact, index) => (
                  <tr
                    key={contact._id || contact.registeredUserId || index}
                    style={{
                      transition: "background 0.15s",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <td className="text-muted" style={{ padding: "0.8rem" }}>
                      {index + 1}
                    </td>
                    <td
                      className="fw-medium"
                      style={{
                        padding: "0.8rem",
                        color: "#0f172a",
                      }}
                    >
                      {contact.contactName}
                    </td>
                    <td style={{ padding: "0.8rem" }}>
                      {contact.contactPhoneNumber}
                    </td>
                    <td style={{ padding: "0.8rem" }}>
                      {contact.registeredUser?.email || (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td style={{ padding: "0.8rem" }}>
                      <Badge
                        bg={contact.isRegistered ? "success" : "secondary"}
                        style={{
                          padding: "0.35rem 1rem",
                          borderRadius: "30px",
                          fontSize: "0.7rem",
                          fontWeight: "500",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {contact.isRegistered ? "Registered" : "Not registered"}
                      </Badge>
                    </td>
                    <td
                      style={{
                        padding: "0.8rem",
                        color: "#475569",
                      }}
                    >
                      {formatDate(contact.addedAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center text-muted py-5"
                    style={{ fontSize: "0.95rem" }}
                  >
                    <div className="py-4">
                      <FiUser
                        size={32}
                        className="text-muted mb-3"
                        style={{ opacity: 0.3 }}
                      />
                      <p className="mb-0">No contacts found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Footer */}
        <Card.Footer
          className="bg-white border-0 px-4 py-3 text-muted"
          style={{
            borderTop: "1px solid #f1f5f9",
            fontSize: "0.8rem",
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <span>
              <span className="fw-semibold" style={{ color: "#0f172a" }}>
                Page {pagination.currentPage}
              </span>{" "}
              of {pagination.totalPages} &middot;
              <span className="ms-2">
                {pagination.totalItems} total contacts
              </span>
            </span>
            <span className="d-flex align-items-center gap-2">
              <span>{pagination.itemsPerPage} per page</span>
              <Badge
                bg="light"
                text="dark"
                className="fw-normal"
                style={{ padding: "0.3rem 0.8rem", borderRadius: "30px" }}
              >
                Next: {pagination.hasNextPage ? "Yes" : "No"}
              </Badge>
              <Badge
                bg="light"
                text="dark"
                className="fw-normal"
                style={{ padding: "0.3rem 0.8rem", borderRadius: "30px" }}
              >
                Prev: {pagination.hasPrevPage ? "Yes" : "No"}
              </Badge>
            </span>
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default ContactsRetrieved;
